import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Box, Button, IconButton, Tooltip, Typography} from "@mui/material";
import axios from "axios";
import {
    Header,
} from "./DataSourceUpdateScheduler.styled";
import {ActionButton} from "../../components/data-sources/DataSourceTable.styled";
import {ReactComponent as Plus2Icon} from "../../assets/icons/plus_2.svg";
import {ReactComponent as InfoSquareIcon} from "../../assets/icons/info_square.svg";
import CreateUpdateSchedulerDialog from "../../components/admin/CreateUpdateSchedulerDialog";
import CronConfigDialog from "../../components/admin/CronConfigDialog";
import {translateCronToHumanReadable} from "../../utils/formatCron";
import {useSnackbar} from "../../contexts/SnackbarContext";
import {JobStatus} from "../../models/jobs.models";
import {parseError} from "../../services/http.service";
import {useTranslation} from 'react-i18next';
import ResponsiveTable, { ResponsiveTableColumn } from "../../components/common/ResponsiveTable";
import {StyledTableContainer} from "../../components/common/TableContainer.styled";

type JobItem = {
    id: number;
    name: string;
    prevFireTime?: string | number | null;
    nextFireTime?: string | number | null;
    isRunning: boolean;
    cronExpression: string;
};

const DataSourceUpdateScheduler: React.FC = () => {
    const [jobs, setJobs] = useState<JobItem[]>([]);
    const [error, setError] = useState("");
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [isCronConfigDialogOpen, setIsCronConfigDialogOpen] = useState(false);
    const [editingJobId, setEditingJobId] = useState<number | null>(null);
    const [editingCronExpression, setEditingCronExpression] = useState<string>("");
    const {showSnackbar} = useSnackbar();
    const {t} = useTranslation();

    const fetchJobs = useCallback(async () => {
        try {
            const response = await axios.get<JobItem[]>("/jobs");
            setJobs(response.data as unknown as JobItem[]);
        } catch (error) {
            console.log(error)
        }
    }, []);

    useEffect(() => {
        fetchJobs();
    }, [fetchJobs]);

    const updateFrequency = useCallback(async (id: number, newCronExpression: string) => {
        try {
            await axios.put(`/jobs/${id}`, {cronExpression: newCronExpression});
            setError("");
            await fetchJobs();
            showSnackbar(t('dataSourceUpdateScheduler.messages.updateSuccess'), 'success');
        } catch (error: unknown) {
            setError(t('dataSourceUpdateScheduler.messages.updateError') as string);
            showSnackbar(parseError(error), "error");
        }
    }, [fetchJobs, showSnackbar, t]);

    const handleOpenCronDialog = useCallback((jobId: number, cronExpression: string) => {
        setEditingJobId(jobId);
        setEditingCronExpression(cronExpression);
        setIsCronConfigDialogOpen(true);
    }, []);

    const handleCloseCronDialog = useCallback(() => {
        setIsCronConfigDialogOpen(false);
        setEditingJobId(null);
        setEditingCronExpression("");
    }, []);

    const handleSaveCron = useCallback((newCron: string) => {
        if (editingJobId !== null) {
            updateFrequency(editingJobId, newCron);
        }
    }, [editingJobId, updateFrequency]);

    const formatDate = useCallback((dateString: string | number | null | undefined) => {
        if (!dateString) return "-";
        const date = new Date(dateString);
        if (isNaN(date.getTime()) || date.getFullYear() < 2000) return "-";
        return date.toLocaleString(undefined, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    }, []);

    const columns: ResponsiveTableColumn<JobItem>[] = useMemo(() => [
        {
            id: 'name',
            header: t('dataSourceUpdateScheduler.tableHeaders.dataSourceName'),
            render: (j) => j.name,
        },
        {
            id: 'lastUpdate',
            header: t('dataSourceUpdateScheduler.tableHeaders.lastUpdate'),
            render: (j) => formatDate(j.prevFireTime ?? null),
        },
        {
            id: 'nextUpdate',
            header: t('dataSourceUpdateScheduler.tableHeaders.nextUpdate'),
            render: (j) => formatDate(j.nextFireTime ?? null),
        },
        {
            id: 'status',
            header: t('dataSourceUpdateScheduler.tableHeaders.status'),
            render: (j) => (j.isRunning ? JobStatus.IN_PROGRESS : JobStatus.SCHEDULED),
        },
        {
            id: 'frequency',
            header: t('dataSourceUpdateScheduler.tableHeaders.frequency'),
            render: (j) => (
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <Typography variant="body2" component="span" sx={{fontFamily: 'monospace'}}>
                        {j.cronExpression}
                    </Typography>
                    <Tooltip title={translateCronToHumanReadable(j.cronExpression)}>
                        <IconButton size="small" sx={{color: 'primary.main'}} aria-label={translateCronToHumanReadable(j.cronExpression)}>
                            <InfoSquareIcon aria-hidden={true} focusable={false} style={{width: 18, height: 18}}/>
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
        },
        {
            id: 'actions',
            header: t('dataSourceUpdateScheduler.tableHeaders.actions'),
            cellClassName: 'actions-cell',
            render: (j) => (
                <Button
                    variant="outlined"
                    onClick={() => handleOpenCronDialog(j.id, j.cronExpression)}
                    size="small"
                    aria-label={t('dataSourceUpdateScheduler.editButton') as string}
                >
                    {t('dataSourceUpdateScheduler.editButton')}
                </Button>
            ),
        },
    ], [t, formatDate, handleOpenCronDialog]);

    return (
        <StyledTableContainer role="region" aria-labelledby="ds-update-scheduler-title">
            <Header>
                <h2 id="ds-update-scheduler-title">{t('dataSourceUpdateScheduler.title')}</h2>
                <ActionButton
                    variant="contained"
                    onClick={() => setIsCreateDialogOpen(true)}
                    startIcon={<Plus2Icon aria-hidden={true} focusable={false}/>} 
                    aria-label={t('dataSourceUpdateScheduler.addButton') as string}
                    sx={{backgroundColor: 'primary.main'}}
                >
                    {t('dataSourceUpdateScheduler.addButton')}
                </ActionButton>
            </Header>
            {error && <Typography color="error">{error}</Typography>}
            <ResponsiveTable
                columns={columns}
                data={jobs}
                getRowKey={(j) => j.id}
                emptyState={t('dataSourceUpdateScheduler.noDataSources')}
                ariaLabel={t('dataSourceUpdateScheduler.title') as string}
            />
            <CreateUpdateSchedulerDialog
                open={isCreateDialogOpen}
                onClose={() => setIsCreateDialogOpen(false)}
                onJobCreated={fetchJobs}
            />
            {editingJobId !== null && (
                <CronConfigDialog
                    open={isCronConfigDialogOpen}
                    currentCron={editingCronExpression}
                    onClose={handleCloseCronDialog}
                    onSave={handleSaveCron}
                />
            )}
        </StyledTableContainer>
    );
};

export default DataSourceUpdateScheduler;
